import { useState, useEffect, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';

const API_BASE = (import.meta.env.VITE_API_URL || 'http://localhost:3005') + '/api';

interface LineRow {
  line: string; revenue: number; direct: number; fees: number;
  allocated: number; contribution: number; marginPct: number;
}
interface PoolRow {
  pool: string; total: number; clubs: number; products: number; schools: number; membership: number;
}
interface UnmappedRow { company: string; name: string; amount: number; }

const fmt = (v: number) => (v < 0 ? '−$' : '$') + Math.abs(v).toLocaleString();

export default function BusinessLines() {
  const [startDate, setStartDate] = useState('2025-11-01');
  const [endDate, setEndDate] = useState('2026-04-30');
  const [lines, setLines] = useState<LineRow[]>([]);
  const [pools, setPools] = useState<PoolRow[]>([]);
  const [unmapped, setUnmapped] = useState<UnmappedRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const r = await fetch(`${API_BASE}/financial-reports/business-lines?startDate=${startDate}&endDate=${endDate}`);
      const j = await r.json();
      if (!j.success) throw new Error(j.error);
      setLines(j.data.lines || []); setPools(j.data.pools || []); setUnmapped(j.data.unmapped || []);
    } catch (e: any) { setError(e.message); }
    setLoading(false);
  }, [startDate, endDate]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Business Lines</h1>
          <p className="text-sm text-gray-500">
            Contribution by line — revenue and direct costs mapped by account (account_line_map), shared pools allocated by the allocation_key table.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="border rounded px-2 py-1 text-sm" />
          <span className="text-gray-400">→</span>
          <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="border rounded px-2 py-1 text-sm" />
          <button onClick={load} className="flex items-center gap-1 bg-blue-600 text-white px-3 py-1.5 rounded text-sm hover:bg-blue-700">
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {error && <div className="bg-red-50 text-red-700 p-3 rounded">{error}</div>}

      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              {['Line', 'Revenue', 'Direct costs', 'Fee realloc', 'Allocated pools', 'Contribution', 'Margin'].map(h => (
                <th key={h} className="px-3 py-2 text-right first:text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {lines.map(l => (
              <tr key={l.line} className="hover:bg-gray-50">
                <td className="px-3 py-2 font-medium">{l.line}</td>
                <td className="px-3 py-2 text-right">{fmt(l.revenue)}</td>
                <td className="px-3 py-2 text-right text-gray-500">{fmt(-l.direct)}</td>
                <td className="px-3 py-2 text-right text-gray-500">{fmt(-l.fees)}</td>
                <td className="px-3 py-2 text-right text-gray-500">{fmt(-l.allocated)}</td>
                <td className={`px-3 py-2 text-right font-bold ${l.contribution >= 0 ? 'text-green-700' : 'text-red-600'}`}>{fmt(l.contribution)}</td>
                <td className={`px-3 py-2 text-right ${l.marginPct >= 0 ? 'text-green-700' : 'text-red-600'}`}>{l.marginPct}%</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="font-semibold mb-2">Shared pools & allocation key</h3>
          <table className="min-w-full text-sm">
            <thead><tr className="text-xs text-gray-500 uppercase">
              <th className="text-left py-1">Pool</th><th className="text-right">Total</th>
              <th className="text-right">Clubs</th><th className="text-right">Prod</th>
              <th className="text-right">Schools</th><th className="text-right">Memb</th>
            </tr></thead>
            <tbody>
              {pools.map(p => (
                <tr key={p.pool} className="border-t border-gray-100">
                  <td className="py-1">{p.pool}</td>
                  <td className="text-right">{fmt(p.total)}</td>
                  <td className="text-right text-gray-500">{Math.round(p.clubs * 100)}%</td>
                  <td className="text-right text-gray-500">{Math.round(p.products * 100)}%</td>
                  <td className="text-right text-gray-500">{Math.round(p.schools * 100)}%</td>
                  <td className="text-right text-gray-500">{Math.round(p.membership * 100)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-gray-400 mt-2">Edit percentages in the allocation_key table; account mapping in account_line_map.</p>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <h3 className="font-semibold mb-2">Unmapped accounts {unmapped.length > 0 && <span className="text-red-600">({unmapped.length})</span>}</h3>
          {unmapped.length === 0
            ? <p className="text-sm text-gray-400">Everything mapped.</p>
            : <div className="text-sm max-h-64 overflow-y-auto">
                {unmapped.map((u, i) => (
                  <div key={i} className="flex justify-between py-1 border-b border-gray-100">
                    <span className="truncate mr-2">{u.name} <span className="text-gray-400 text-xs">({u.company})</span></span>
                    <span className="whitespace-nowrap">{fmt(u.amount)}</span>
                  </div>
                ))}
              </div>}
        </div>
      </div>
      <p className="text-xs text-gray-400 text-gray-400">
        This is the account-mapped approximation of the July 2026 tagged model. Custom Clubs margin is overstated while Callaway
        costs are booked on payment — reconcile account 2100 to the Callaway statement for true margins (15–36% range).
      </p>
    </div>
  );
}
